import React, { Suspense, lazy } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import Header from "@/components/Header";
import { FileText, Calculator, ShieldCheck, ArrowRight, Cpu, Users, Star, Lock, Shield } from "lucide-react";
import { motion } from "framer-motion";
import { SEO } from "@/components/SEO";

const Footer = lazy(() => import("@/components/Footer"));

const MobileTaxReturnLanding = () => {
  const processSteps = [
    {
      icon: FileText,
      title: "1. Provide Details",
      description: "Fill out our secure form with your income details.",
    },
    {
      icon: Cpu,
      title: "2. We Process",
      description: "Experts accurately process your return for compliance.",
    },
    {
      icon: ShieldCheck,
      title: "3. Confirmation",
      description: "Get FBR confirmation, entirely handled by us.",
    },
  ];

  const fadeUp = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  return (
    <div className="min-h-[100dvh] bg-[#f8fafc] flex flex-col font-sans text-slate-900 pb-0">
      <SEO 
        title="Tax Return Info - Make Me Filer" 
        description="Intelligent Tax Filing, Engineered for Clarity."
      />
      <Header />

      <main className="flex-1 w-full pt-[80px]">
        {/* Dynamic Hero Section */}
        <section className="relative px-5 pt-8 pb-10 overflow-hidden bg-slate-900 rounded-b-[2.5rem] shadow-[0_15px_40px_rgba(15,23,42,0.15)] mx-2 z-10">
          <div className="absolute inset-0 w-full h-full mix-blend-overlay">
            <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950"></div>
          </div>
          
          <div className="absolute top-[-20%] right-[-10%] w-[300px] h-[300px] bg-blue-500/20 rounded-full blur-[60px] pointer-events-none"></div>

          <motion.div initial="hidden" animate="visible" variants={fadeUp} className="relative z-10">
            <div className="mt-2 mb-5 inline-flex items-center gap-1.5 bg-blue-900/40 backdrop-blur-md text-blue-200 border border-blue-500/30 px-3 py-1.5 rounded-full text-[11px] font-black tracking-wider shadow-sm">
               <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" /> Professional Filing
            </div>

            <h1 className="text-[2.5rem] font-extrabold text-white leading-[1.05] tracking-tight mb-4">
               Intelligent <br/><span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-300">Tax Filing.</span>
            </h1>
            <p className="text-[15px] text-slate-300 font-medium leading-relaxed mb-6 pe-4">
               Leverage our advanced platform for seamless FBR compliance and annual return filing.
            </p>

            <Button className="w-full h-14 rounded-2xl bg-blue-600 hover:bg-blue-500 text-white text-base font-bold shadow-[0_10px_25px_rgba(37,99,235,0.4)] active:scale-[0.98] transition-transform" asChild>
              <Link to="/tax-return" className="flex items-center justify-center gap-2">
                 <FileText className="w-5 h-5" /> Start Filing Now
              </Link>
            </Button>

            <div className="flex flex-wrap gap-2.5 mt-6 justify-start">
              <div className="flex items-center gap-1.5 bg-slate-800/60 backdrop-blur-sm px-3 py-2 rounded-[10px] border border-slate-700/50">
                <Shield className="w-3.5 h-3.5 text-emerald-400" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-200">FBR Compliant</span>
              </div>
              <div className="flex items-center gap-1.5 bg-slate-800/60 backdrop-blur-sm px-3 py-2 rounded-[10px] border border-slate-700/50">
                <Lock className="w-3.5 h-3.5 text-blue-400" />
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-200">100% Secure</span>
              </div>
            </div>
          </motion.div>
        </section>

        {/* Process Steps */}
        <section className="px-5 pt-8 pb-4 relative z-20">
           <div className="mb-6">
             <h2 className="text-[1.35rem] font-black text-slate-900 tracking-tight leading-none mb-1.5">A Smarter Way</h2>
             <p className="text-[13px] text-slate-500 font-medium leading-none">Our 3-step process simplifies everything.</p>
           </div>
           
           <div className="flex flex-col gap-4">
             {processSteps.map((step, idx) => (
                <div key={idx} className="bg-white rounded-3xl border border-slate-100 p-5 shadow-sm flex items-start gap-4">
                   <div className="w-12 h-12 bg-blue-50 border border-blue-100/50 rounded-2xl flex-shrink-0 flex items-center justify-center">
                     <step.icon className="w-6 h-6 text-blue-600" />
                   </div>
                   <div className="flex-1">
                     <h3 className="text-[15px] font-black text-slate-900 mb-1 leading-tight">{step.title}</h3>
                     <p className="text-xs text-slate-500 leading-relaxed pr-2">{step.description}</p>
                   </div>
                </div>
             ))}
           </div>
        </section>

        {/* Action Cards */}
        <section className="px-5 py-6 pb-16">
          <div className="grid grid-cols-1 gap-4">
            <Link to="/tax-calculator" className="relative group bg-gradient-to-br from-indigo-500 to-blue-600 rounded-3xl p-6 shadow-lg overflow-hidden flex flex-col justify-between min-h-[160px] active:scale-[0.98] transition-transform">
               <div className="absolute top-[-20%] right-[-10%] w-[150px] h-[150px] bg-white/10 rounded-full blur-[40px] pointer-events-none"></div>
               <Calculator className="w-8 h-8 text-white mb-4 opacity-90" />
               <div>
                 <h3 className="text-lg font-bold text-white mb-1">Tax Calculator</h3>
                 <p className="text-sm text-blue-100 mb-4 opacity-90">Estimate your tax liability easily.</p>
                 <div className="flex items-center text-sm font-bold text-white">
                   Calculate <ArrowRight className="w-4 h-4 ml-1.5" />
                 </div>
               </div>
            </Link>

            <Link to="/contact" className="relative group bg-white rounded-3xl border border-slate-100 p-6 shadow-sm overflow-hidden flex flex-col justify-between min-h-[160px] active:scale-[0.98] transition-transform">
               <Users className="w-8 h-8 text-blue-600 mb-4" />
               <div>
                 <h3 className="text-lg font-bold text-slate-900 mb-1">Expert Help</h3>
                 <p className="text-sm text-slate-500 mb-4">Connect with a certified professional.</p>
                 <div className="flex items-center text-sm font-bold text-blue-600">
                   Contact Us <ArrowRight className="w-4 h-4 ml-1.5" />
                 </div>
               </div>
            </Link>
          </div>
        </section>

      </main>

      <Suspense fallback={<div className="h-24 bg-slate-100 w-full animate-pulse" />}>
         <Footer />
      </Suspense>
    </div>
  );
};

export default MobileTaxReturnLanding;
